import { Close } from '@mui/icons-material';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import {
  AsyncLoadingSection,
  ConfirmationModal,
  ModalDrawerButtonRow,
  ModalFormDrawer,
  Render,
} from 'Components/Display';
import {
  FormSubmitButton,
  ModalFormCancelButton,
  SecondaryButton,
  SmartInputBlock,
} from 'Components/Form';
import ButtonWithTooltip from 'Components/Form/ButtonWithTooltip';
import { SecureComponent } from 'Components/Security';
import {
  EntityActionTypes,
  FormActionTypes,
  useEntity,
  useEntityContext,
  useFormStateValue,
  useFormUpdater,
  useModalFormState,
  useToasts,
} from 'Context';
import { ColSize, InputType, Referral } from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  buildEndpoint,
  useApiWorker,
  useDataFetcher,
  useDataPoster,
  useToggler,
} from 'Utilities';
import { PrimaryButton, SecondaryButton as SecondatyButtonV3 } from 'v3/Components/Shared/Buttons';

export const CloseReferralDialog: React.FC = () => {
  const entity = useEntity();
  const [appointmentReportBundles, setAppointmentReportBundles] = useState<[]>(
    []
  );
  const API = useApiWorker();

  useEffect(() => {
    if (entity.appointment?.id) {
      API.post(
        `${ENDPOINTS.appointmentsreportsbundles.read}?page=1&pageSize=50`,
        {
          appointmentId: entity.appointment?.id,
        }
      ).then((res) => {
        setAppointmentReportBundles(res.data?.data || []);
      });
    }
  }, [entity.appointment?.id]);

  const reviewedBy = useMemo(
    () =>
      appointmentReportBundles?.every(
        (report: any) =>
          report.reviewedById != null &&
          report.correctCredentials &&
          report.reviewed &&
          report.attestingById != null &&
          report.noConflicts
      ),

    [appointmentReportBundles]
  );

  const { toggle, setEntityId } = useModalFormState();

  const caseStatus = entity.caseStatus?.displayName;

  let allReviewed =
    entity.appointment?.appointmentReportBundles.length > 0 &&
    entity.appointment?.appointmentReportBundles?.every(
      (report: any) => report.reviewed
    );
  let warningIndicator = allReviewed ? '' : 'warning';
  let missingReviews = allReviewed ? '' : 'Some reports have not been reviewed';
  const [toggler, setToggler] = useToggler();
  const [ShowModal, setShowModal] = useState(false);
  const handleModalDismiss = () => {
    setShowModal(false);
  };


  return (
    <Fragment>
      <Render condition={caseStatus != 'Closed'}>
        <Tooltip openDelay={100} position="bottom" anchorElement="target">
          <SecureComponent permissions={[Permission.CloseReferral]}>
            {!reviewedBy ? (
              <ButtonWithTooltip
                title="Case cannot be completed until doctor attestation is checked."
                tooltipProps={{ placement: 'bottom' }}
                wrapperClassName="float-right"
              >
                <SecondaryButton disabled>Complete Case</SecondaryButton>
              </ButtonWithTooltip>
            ) : (
              <SecondaryButton
                title={missingReviews}
                className="d-inline float-right buttons-container-button"
                onClick={() => {
                  setShowModal(true);
                  toggle();
                }}
                icon={warningIndicator}
              >
                {' '}
                Complete case
              </SecondaryButton>
            )}
          </SecureComponent>
        </Tooltip>
        <CloseCaseDialog isOpen={ShowModal} toggle={handleModalDismiss} referralId={entity.id} />
      </Render>
      <Render condition={caseStatus == 'Closed'}>
        <SecureComponent permissions={[Permission.ReopenReferral]}>
          <SecondaryButton
            title={missingReviews}
            className="d-inline float-right buttons-container-button"
            onClick={setToggler}
            icon={warningIndicator}
          >
            {' '}
            Reopen Case
          </SecondaryButton>

          <ConfirmReopenDialog isOpen={toggler} toggler={setToggler} />
        </SecureComponent>
      </Render>
    </Fragment>
  );
};

const ConfirmReopenDialog: React.FC<{
  isOpen: boolean;
  toggler: () => void;
}> = ({ isOpen, toggler }) => {
  const [ent, dispatcher] = useEntityContext();
  const entity = useEntity();

  const onSuccess = (response: any) => {
    ent.caseStatus = { displayName: 'Reopened', value: 3 };
    ent.appointment.appointmentStatus = { displayName: 'Re-opened', value: 4 };
    dispatcher({ type: EntityActionTypes.SetEntity, payload: ent });
  };

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.referrals.endpointString('reopenReferral')),
    onSuccess
  );

  const onConfirm = async () => {
    postData({ id: entity.id });
  };

  return (
    <ConfirmationModal
      toggle={toggler}
      isOpen={isOpen}
      confirmText="Are you sure that you want to reopen the case? This will reset its status"
      onConfirm={onConfirm}
    ></ConfirmationModal>
  );
};
interface CloseReferralModalProps {
  isOpen: boolean;
  toggle: () => void;
  referralId: string;
}
const CloseReferralDrawerContent: React.FC = () => {
  const { entityId, isOpen } = useModalFormState();

  const sendUpdate = useFormUpdater();
  const entity = useEntity();
  const closedDate = useFormStateValue('closedDate');
  const reviewed = useFormStateValue('reviewed');
  const systemToast = useToasts();
  const [ent, dispatcher] = useEntityContext();

  const addOrEditEndpoint = buildEndpoint(
    ENDPOINTS.referrals.endpointString('CloseReferral')
  );

  let [referralDocumentModel, fetchStatus] = useDataFetcher<Referral>(
    buildEndpoint(
      ENDPOINTS.referrals.endpointString('getCloseCommandQuery'),
      entity.id
    ),
    new Referral(),
    ({ data }) => data,
    undefined,
    isOpen
  );

  const onFormSubmit = (data?: any, handler?: any) => {
    handler(data);
    ent.caseStatus = { displayName: 'Closed', value: 2 };
    ent.appointment.appointmentStatus = { value: 5, displayName: 'Closed' };
    ent.closedDate = closedDate;
    ent.appointment.reviewed = reviewed;
    dispatcher({ type: EntityActionTypes.SetEntity, payload: ent });
  };

  const handleError = (data: { Errors: string; Value: string }) => {
    if (data.Errors) {
      if (data.Errors.startsWith('Validation Error: At least')) {
        toast.error(
          <div className="font-weight-bold">
            The case could not be closed because a suitable report could not be
            found that meets the validation criteria:
            <div className="mt-2 font-weight-normal">
              <ul>
                <li>
                  Has at least one report marked as reviewed, and with the
                  'Reviewed By' field filled in
                </li>
                <li>
                  The same report has the determination, history of condition,
                  conclusion, source criteria, citation, citation guidelines and
                  guideline filled in
                </li>
              </ul>
            </div>
          </div>,
          { autoClose: 10000 }
        );
      } else {
        systemToast.error(data.Errors);
      }
    }
  };
  useEffect(() => {
    sendUpdate(FormActionTypes.SetValues, referralDocumentModel);
  }, [referralDocumentModel, sendUpdate]);

  return (
    <AsyncLoadingSection loadingStatus={fetchStatus} noIdleLoading>
      <SmartInputBlock
        name="closedDate"
        label="Case Closed Date"
        type={InputType.DatePicker}
        colProps={{ xs: ColSize.Half }}
        dateOnly
      />
      <SmartInputBlock
        name="reviewed"
        label="Case Reviewed"
        type={InputType.Checkbox}
        colProps={{ xs: ColSize.Half }}
      />
      <ModalDrawerButtonRow>
        <ModalFormCancelButton />
        <FormSubmitButton
          alternateEndpoint={addOrEditEndpoint}
          alternateHandleSuccess={onFormSubmit}
          alternateHandleError={handleError}
        >
          Close Case
        </FormSubmitButton>
      </ModalDrawerButtonRow>
    </AsyncLoadingSection>
  );
};

const CloseCaseDialog: React.FC<CloseReferralModalProps> = (
  {
    isOpen,
    toggle,
    referralId,
  }
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API = useApiWorker();
  const dialogTitle = 'Complete Case';
  const [ent, dispatcher] = useEntityContext();
  const entity = useEntity();

  const onSuccess = (response: any) => {
    ent.caseStatus = { displayName: 'Closed', value: 2 };
    ent.appointment.appointmentStatus = { displayName: 'Closed', value: 5 };
    dispatcher({ type: EntityActionTypes.SetEntity, payload: ent });
  };
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const todayDateUtc = new Date();
      const response = await API.post(ENDPOINTS.referrals.endpointString('CloseReferral'), {
        id: referralId,
        closedDate: todayDateUtc.toISOString(),
        reviewed: true
      });

      if (response.status === 200) {
        onSuccess(response);
        toggle();
      }
    } catch (error) {
      console.error('Error claiming ownership:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    //closing dialog

    toggle();
  };

  if (!isOpen) return null;

  return (
    <Dialog className="referral-ownership-dialog" onClose={handleDecline}>
      <div className="dialog-header">
        <div className="dialog-title">{dialogTitle}</div>
        <div className="dialog-close" onClick={handleDecline}>
          <span className="dialog-close-icon">
            <Close />
          </span>
        </div>
      </div>
      <div className="dialog-row">
        <div className="dialog-column">
          <p>
            Are you sure that you want to close this case?
          </p>
        </div>
      </div>
      <div className="dialog-footer">
        <SecondatyButtonV3
          className="centered"
          onClick={handleDecline}
          disabled={isSubmitting}
        >
          <div className="title">No</div>
        </SecondatyButtonV3>
        <PrimaryButton
          className={`centered ${isSubmitting ? 'disabled' : ''}`}
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          <div className="title">
            {isSubmitting ? 'Closing Case...' : 'Close Case'}
          </div>
        </PrimaryButton>
      </div>
    </Dialog>
  );
}